import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAddExperience, useUpdateExperience } from '../hooks';
import type { CandidateExperience } from '../types';

interface ExperienceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateId: number;
  experience?: CandidateExperience | null;
}

const months = [
  { value: '01', label: 'Janvier' },
  { value: '02', label: 'Février' },
  { value: '03', label: 'Mars' },
  { value: '04', label: 'Avril' },
  { value: '05', label: 'Mai' },
  { value: '06', label: 'Juin' },
  { value: '07', label: 'Juillet' },
  { value: '08', label: 'Août' },
  { value: '09', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 50 }, (_, i) => String(currentYear - i));

const experienceSchema = z.object({
  company: z.string().min(1, 'L\'entreprise est requise').max(255),
  title: z.string().min(1, 'Le poste est requis').max(255),
  startMonth: z.string().min(1, 'Le mois est requis'),
  startYear: z.string().min(1, 'L\'année est requise'),
  endMonth: z.string().optional().or(z.literal('')),
  endYear: z.string().optional().or(z.literal('')),
  isCurrent: z.boolean().optional(),
  location: z.string().max(255).optional().or(z.literal('')),
  missions: z.string().optional().or(z.literal('')),
  techEnv: z.string().optional().or(z.literal('')),
}).refine(
  (data) => {
    if (data.isCurrent || !data.endMonth || !data.endYear) return true;
    const start = parseInt(data.startYear) * 12 + parseInt(data.startMonth);
    const end = parseInt(data.endYear) * 12 + parseInt(data.endMonth);
    return end >= start;
  },
  { message: 'La date de fin doit être postérieure à la date de début', path: ['endYear'] }
);

type ExperienceFormData = z.infer<typeof experienceSchema>;

/**
 * Parse a structured markdown description into 2 sections.
 * Supports both old format (**Résumé** + **Contexte du projet**) and
 * new format (**Missions & Responsabilités**).
 */
function parseDescription(description: string | null): { missions: string; techEnv: string } {
  if (!description) return { missions: '', techEnv: '' };

  const sections = { missions: '', techEnv: '' };

  // New format: **Missions & Responsabilités**
  const missionsMatch = description.match(/\*\*Missions & Responsabilités\*\*\s*\n([\s\S]*?)(?=\n\*\*Environnement technique\*\*|$)/);
  // Old format: **Résumé** and **Contexte du projet**
  const summaryMatch = description.match(/\*\*Résumé\*\*\s*\n([\s\S]*?)(?=\n\*\*Contexte du projet\*\*|\n\*\*Environnement technique\*\*|$)/);
  const contextMatch = description.match(/\*\*Contexte du projet\*\*\s*\n([\s\S]*?)(?=\n\*\*Environnement technique\*\*|$)/);
  const techMatch = description.match(/\*\*Environnement technique\*\*\s*\n([\s\S]*?)$/);

  if (missionsMatch) {
    sections.missions = missionsMatch[1].trim();
  } else if (summaryMatch || contextMatch) {
    // Merge old summary + context into missions
    const parts: string[] = [];
    if (summaryMatch) parts.push(summaryMatch[1].trim());
    if (contextMatch) parts.push(contextMatch[1].trim());
    sections.missions = parts.filter(Boolean).join('\n\n');
  }

  if (techMatch) sections.techEnv = techMatch[1].trim();

  // If no sections matched, treat the whole text as missions
  if (!missionsMatch && !summaryMatch && !contextMatch && !techMatch && description.trim()) {
    sections.missions = description.trim();
  }

  return sections;
}

/**
 * Combine 2 sections into a structured markdown description.
 */
function buildDescription(missions: string, techEnv: string): string | undefined {
  if (!missions && !techEnv) return undefined;

  const parts: string[] = [];
  if (missions) parts.push(`**Missions & Responsabilités**\n${missions}`);
  if (techEnv) parts.push(`**Environnement technique**\n${techEnv}`);

  return parts.join('\n\n');
}

/** Extract month (01-12) from an ISO date string */
function extractMonth(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return String(d.getMonth() + 1).padStart(2, '0');
}

/** Extract year from an ISO date string */
function extractYear(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return String(d.getFullYear());
}

export function ExperienceFormDialog({
  open,
  onOpenChange,
  candidateId,
  experience,
}: ExperienceFormDialogProps) {
  const addExperience = useAddExperience();
  const updateExperience = useUpdateExperience();
  const isEditing = !!experience;
  const isPending = addExperience.isPending || updateExperience.isPending;

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<ExperienceFormData>({
    resolver: zodResolver(experienceSchema),
    defaultValues: {
      company: '',
      title: '',
      startMonth: '',
      startYear: '',
      endMonth: '',
      endYear: '',
      isCurrent: false,
      location: '',
      missions: '',
      techEnv: '',
    },
  });

  const isCurrent = watch('isCurrent');

  useEffect(() => {
    if (open) {
      if (experience) {
        const { missions, techEnv } = parseDescription(experience.description);
        reset({
          company: experience.company,
          title: experience.title,
          startMonth: extractMonth(experience.startDate),
          startYear: extractYear(experience.startDate),
          endMonth: extractMonth(experience.endDate),
          endYear: extractYear(experience.endDate),
          isCurrent: experience.isCurrent,
          location: experience.location || '',
          missions,
          techEnv,
        });
      } else {
        reset({
          company: '',
          title: '',
          startMonth: '',
          startYear: '',
          endMonth: '',
          endYear: '',
          isCurrent: false,
          location: '',
          missions: '',
          techEnv: '',
        });
      }
    }
  }, [open, experience, reset]);

  useEffect(() => {
    if (isCurrent) {
      setValue('endMonth', '');
      setValue('endYear', '');
    }
  }, [isCurrent, setValue]);

  const onSubmit = async (data: ExperienceFormData) => {
    const description = buildDescription(data.missions || '', data.techEnv || '');

    const startDate = `${data.startYear}-${data.startMonth}-01`;

    let endDate: string | undefined;
    if (!data.isCurrent && data.endMonth && data.endYear) {
      endDate = `${data.endYear}-${data.endMonth}-01`;
    }

    const dto = {
      company: data.company,
      title: data.title,
      description,
      startDate,
      endDate: endDate ?? undefined,
      isCurrent: data.isCurrent || false,
      location: data.location || undefined,
    };

    if (isEditing && experience) {
      await updateExperience.mutateAsync({ candidateId, experienceId: experience.id, dto });
    } else {
      await addExperience.mutateAsync({ candidateId, dto });
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Modifier l\'expérience' : 'Ajouter une expérience'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company">Entreprise *</Label>
              <Input id="company" {...register('company')} placeholder="Nom de l'entreprise" />
              {errors.company && <p className="text-xs text-destructive">{errors.company.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Poste *</Label>
              <Input id="title" {...register('title')} placeholder="Intitulé du poste" />
              {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date de début *</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select
                  value={watch('startMonth')}
                  onValueChange={(value) => setValue('startMonth', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Mois" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={watch('startYear')}
                  onValueChange={(value) => setValue('startYear', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Année" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(errors.startMonth || errors.startYear) && (
                <p className="text-xs text-destructive">La date de début est requise</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Date de fin</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select
                  value={watch('endMonth') || ''}
                  onValueChange={(value) => setValue('endMonth', value)}
                  disabled={!!isCurrent}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Mois" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={watch('endYear') || ''}
                  onValueChange={(value) => setValue('endYear', value)}
                  disabled={!!isCurrent}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Année" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {errors.endYear && (
                <p className="text-xs text-destructive">{errors.endYear.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isCurrent"
                {...register('isCurrent')}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="isCurrent" className="font-normal">Poste actuel</Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Lieu</Label>
              <Input id="location" {...register('location')} placeholder="Paris, France" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="missions">Missions & Responsabilités</Label>
            <Textarea
              id="missions"
              {...register('missions')}
              placeholder="Missions, tâches et responsabilités..."
              rows={5}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="techEnv">Environnement technique</Label>
            <Textarea
              id="techEnv"
              {...register('techEnv')}
              placeholder="Technologies et outils utilisés..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
